/**
 * AiEnginePanel — the machinery behind both AI systems, on one tab.
 *
 * WHY THIS EXISTS
 *
 * There were two AI pages. `/edge-ai` ran a self-trained statistical ranker (Pragya) and `/ai-desk`
 * runs an LLM layer over RockBand signals, and a reader had no way to tell which one to open.
 *
 * The Edge BOARD has now been judged on its own numbers and did not survive: 1,492 resolved picks,
 * direction accuracy 18% against its own baseline of 17% — +0.8 pp, which at that n is inside the
 * noise — with 67% of picks producing no move at all and zero real trades ever taken on one. Both
 * horizons' models are STALE, active since 24-25 July, with 57 and 56 consecutive retrains rejected
 * by the promotion gate. The gate is working; there is simply nothing better to promote.
 *
 * That is NOT the same as "the ranker is worthless", and the §7 finding it would otherwise
 * contradict still stands: it trains on 42,078 and 41,211 rows, not the 120 the original brief
 * assumed. What changed is that we now have realised outcomes rather than a training-set lift, and
 * the realised edge is not there. So the board stops being presented as decision-grade output while
 * the corpus, the pipeline and every table stay exactly where they are.
 *
 * WHAT MOVED HERE, AND WHY EACH EARNED IT
 *
 * - **Quant-ranker health** — promoted from a one-line chip strip on the Signals tab. Staleness and
 *   a rejection streak are the two facts that decide whether to believe the board at all, and they
 *   were being rendered as two small orange chips.
 * - **The pipeline controls** — capture, label, retrain. The corpus keeps growing whether or not
 *   the board is shown, and it is the thing a future promotion would come from.
 *
 * WHAT DID NOT MOVE: the Edge Board itself, its paper Auto-Trade ledger, its Option Trades table
 * (the Option Desk already does that with expectancy and probability of profit), Accuracy and
 * Why & Model. Three separate "did it work?" surfaces across two pages was the actual problem.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Chip, Button, Tooltip, CircularProgress, Table, TableHead,
  TableRow, TableCell, TableBody, Alert as MuiAlert,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAppTheme } from '../../hooks/useAppTheme';
import aiService, { type AiQuantHealth } from '../../services/ai.service';
import { edgeService } from '../../services/edge.service';
import HelpLink from '../common/HelpLink';
import toast from 'react-hot-toast';

const AiEnginePanel: React.FC = () => {
  const { C } = useAppTheme();
  const [quant, setQuant] = useState<AiQuantHealth[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      aiService.getQuantHealth().catch(() => []),
      edgeService.getStats().catch(() => null),
    ]).then(([q, s]) => { setQuant(q || []); setStats((s as any)?.data?.data ?? null); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const run = async (what: 'capture' | 'label' | 'retrain') => {
    setBusy(what);
    try {
      if (what === 'capture') await edgeService.capture();
      else if (what === 'label') await edgeService.label();
      else await edgeService.retrain();
      toast.success(`${what} finished`);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || `${what} failed`);
    } finally { setBusy(null); }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  }

  const anyStale = quant.some(q => q.state === 'STALE' || q.state === 'NO_ACTIVE_MODEL');

  return (
    <Box>
      <MuiAlert severity="info" variant="outlined" sx={{ mb: 2, py: 0.4 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
          The statistical ranker that used to have its own page. It is <b>not</b> used to rank the
          board — RockBand generates the candidates and the model layer judges them. It is kept
          because it keeps learning from a corpus of {stats?.totalSnapshots?.toLocaleString?.('en-IN') || '—'} snapshots,
          and because its forward-range bands feed the Option Desk.
          {' '}<HelpLink to="ai-desk/engine" variant="inline" label="what this engine is for" />
        </Typography>
      </MuiAlert>

      {/* ── Corpus ─────────────────────────────────────────────────────────── */}
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.8 }}>What it has learned from</Typography>
      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 2.5 }}>
        {[
          ['Snapshots', stats?.totalSnapshots?.toLocaleString?.('en-IN') ?? '—',
            'Indicator readings captured across every tracked symbol and timeframe. This is the training corpus.'],
          ['Symbols', String(stats?.distinctSymbols ?? '—'),
            'How many distinct symbols the capture covers.'],
          ['Last capture', stats?.lastCapture ? new Date(stats.lastCapture).toLocaleString('en-IN') : '—',
            'When the corpus last grew. A capture that has stopped is the quietest way for a model to go stale.'],
          ['Schema', `v${stats?.schemaVersion ?? '—'}`,
            'The feature-set version. A schema change means older rows carry different columns, which is why the trainer tracks it.'],
        ].map(([label, value, hint]) => (
          <Tooltip key={label} arrow title={hint}>
            <Box sx={{ minWidth: 110, cursor: 'help' }}>
              <Typography variant="caption" sx={{ color: C.dim, display: 'block' }}>{label}</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>{value}</Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>

      {/* ── Model health — the two facts that decide whether to believe it ──── */}
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.8 }}>
        Model health
        <Tooltip arrow title="Two things matter here and neither is the accuracy number: how OLD the active model is, and how many retrains the promotion gate has rejected since. A long rejection streak is the gate working — it refuses to promote a model that does not beat the one in place — but it also means the active model is being asked to cover a market it was not trained on.">
          <InfoOutlinedIcon sx={{ fontSize: 14, color: C.dim, ml: 0.5, cursor: 'help', verticalAlign: 'middle' }} />
        </Tooltip>
      </Typography>

      {anyStale && (
        <MuiAlert severity="warning" variant="outlined" sx={{ mb: 1.5, py: 0.3 }}>
          <Typography variant="caption" sx={{ lineHeight: 1.45 }}>
            At least one horizon is stale. The promotion gate requires a challenger to beat the
            active model on lift, per direction, with calibration inside tolerance — so a long
            rejection streak means nothing better has been found, not that training stopped.
          </Typography>
        </MuiAlert>
      )}

      <Box sx={{ overflowX: 'auto', mb: 2.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                ['Horizon', 'Which forward window this model predicts.'],
                ['State', 'OK, or STALE when the active model is older than the refresh interval.'],
                ['Active', 'The model version currently making predictions, and when it was trained.'],
                ['Age', 'Days since the active model was trained.'],
                ['Rejected since', 'Retrains attempted and refused by the promotion gate since this one was adopted.'],
                ['Lift (up / down)', 'How much better than the base rate the top-ranked picks are, per direction. 1.0 is no edge.'],
                ['Why not promoted', 'The gate’s own words about the most recent rejected challenger.'],
              ].map(([h, hint]) => (
                <Tooltip key={h} arrow title={hint}>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', cursor: 'help' }}>{h}</TableCell>
                </Tooltip>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!quant.length ? (
              <TableRow><TableCell colSpan={7}>
                <Typography variant="caption" sx={{ color: C.dim }}>No trained model on record yet.</Typography>
              </TableCell></TableRow>
            ) : quant.map(q => (
              <TableRow key={q.horizon}>
                <TableCell sx={{ fontWeight: 700 }}>{q.horizon.replace(/_/g, ' ').toLowerCase()}</TableCell>
                <TableCell>
                  <Chip size="small" label={q.state} sx={{
                    height: 19, fontSize: '0.65rem', fontWeight: 700,
                    bgcolor: q.state === 'OK' ? C.bullMuted : C.warnMuted,
                    color: q.state === 'OK' ? C.bull : C.warn,
                  }} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  v{q.activeVersion ?? '—'}
                  <Typography component="span" variant="caption" sx={{ color: C.dim, ml: 0.6 }}>
                    {q.activeTrainedAt ? new Date(q.activeTrainedAt).toLocaleDateString('en-IN') : ''}
                  </Typography>
                </TableCell>
                <TableCell sx={{ color: (q.activeAgeDays ?? 0) > 30 ? C.warn : undefined, fontWeight: 700 }}>
                  {q.activeAgeDays ?? '—'}d
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{q.rejectedSince ?? '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {q.upLift != null ? q.upLift.toFixed(2) : '—'} / {q.downLift != null ? q.downLift.toFixed(2) : '—'}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ color: C.dim, lineHeight: 1.4 }}>
                    {q.latestBlockReason || '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* ── Pipeline ───────────────────────────────────────────────────────── */}
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.8 }}>
        Pipeline
        <Tooltip arrow title="These run on a schedule already. The buttons are for forcing a cycle by hand — after adding symbols to the universe, say, or to see whether a retrain would now clear the gate. None of them calls a language model, so none of them costs anything.">
          <InfoOutlinedIcon sx={{ fontSize: 14, color: C.dim, ml: 0.5, cursor: 'help', verticalAlign: 'middle' }} />
        </Tooltip>
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {([
          ['capture', 'Capture now', 'Reads the current indicator state for every symbol in the universe and appends it to the corpus.'],
          ['label', 'Label now', 'Goes back over captured rows whose forward window has closed and records what actually happened.'],
          ['retrain', 'Retrain now', 'Trains a challenger on the labelled corpus and offers it to the promotion gate. The active model is only replaced if the challenger beats it.'],
        ] as const).map(([id, label, hint]) => (
          <Tooltip key={id} arrow title={`${hint} Costs nothing — no model call.`}>
            <span>
              <Button size="small" variant="outlined" disabled={!!busy} onClick={() => run(id)}>
                {busy === id ? 'Running…' : label} · ₹0
              </Button>
            </span>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

export default AiEnginePanel;
